import { useQuery } from '@tanstack/react-query';
import { useCallback } from 'react';
import { z } from 'zod';

import { apiRequest } from '@/api/client';
import { useI18n, useT } from '@/lib/i18n';

/**
 * The industry taxonomy a merchant picks at onboarding. The keys are what the
 * API stores on the tenant; a key is never shown.
 *
 * The list is the platform's (`GET /industries`, kept in the database and
 * edited from the platform panel), read through {@link useIndustries}, and
 * each industry carries its own English and Hindi names. The list below - the
 * 22 NeonCarts launched with, named through the `onboarding` dictionaries -
 * only stands in until that list arrives, or if it cannot.
 *
 * Themes are filed under these same keys, plus {@link GENERAL_INDUSTRY}.
 */

export const SECTORS = ['retail', 'food', 'health_wellness', 'services'] as const;

export type Sector = (typeof SECTORS)[number];

/** The key a theme is filed under when it suits any shop. Not an industry a store picks. */
export const GENERAL_INDUSTRY = 'general';

export interface Industry {
  key: string;
  sector: Sector;
  /** Its names, from the platform's list. Absent on the offline fallback. */
  labelEn?: string | null;
  labelHi?: string | null;
  /** False for a retired industry the store is already on. */
  active?: boolean;
}

export const INDUSTRIES: readonly Industry[] = [
  // Retail
  { key: 'fashion_apparel', sector: 'retail' },
  { key: 'beauty_cosmetics', sector: 'retail' },
  { key: 'jewellery', sector: 'retail' },
  { key: 'electronics', sector: 'retail' },
  { key: 'home_kitchen', sector: 'retail' },
  { key: 'furniture', sector: 'retail' },
  { key: 'sports', sector: 'retail' },
  { key: 'books_stationery', sector: 'retail' },
  { key: 'toys_baby', sector: 'retail' },
  { key: 'automotive', sector: 'retail' },
  { key: 'pet_supplies', sector: 'retail' },
  // Food
  { key: 'grocery', sector: 'food' },
  { key: 'bakery', sector: 'food' },
  { key: 'restaurant_food_delivery', sector: 'food' },
  { key: 'flowers_gifts', sector: 'food' },
  // Health & Wellness
  { key: 'health_wellness', sector: 'health_wellness' },
  { key: 'pharmacy_medical', sector: 'health_wellness' },
  // Services
  { key: 'salon_beauty_services', sector: 'services' },
  { key: 'clinics', sector: 'services' },
  { key: 'consultancy', sector: 'services' },
  { key: 'repair_maintenance', sector: 'services' },
  { key: 'other_services', sector: 'services' },
];

export const INDUSTRY_KEYS: readonly string[] = INDUSTRIES.map((industry) => industry.key);

export function findIndustry(
  key: string | null | undefined,
  list: readonly Industry[] = INDUSTRIES,
): Industry | undefined {
  return key ? list.find((industry) => industry.key === key) : undefined;
}

/**
 * The wording variants for a store in `industry`, most specific first: the
 * industry itself, then its sector (`['clinics', 'sector_services']`). What
 * the dashboard's translator tries before the plain text, so a clinic sees
 * "Our services" where a shop sees "Products" (`dictionaries/<lang>/industryTerms.ts`).
 *
 * An industry the platform added after this build has no rewordings of its
 * own and no sector here, so it keeps the plain wording.
 */
export function industryVariants(industry: string | null | undefined): string[] {
  if (!industry) return [];
  const known = findIndustry(industry);
  return known ? [known.key, `sector_${known.sector}`] : [industry];
}

/**
 * What to call an industry: its own name from the platform's list (Hindi in
 * Hindi, English everywhere else), else - on the offline fallback - its
 * translated name.
 */
export function industryLabel(
  industry: Industry,
  t: (key: string) => string,
  language: string,
): string {
  if (industry.labelEn) {
    return language === 'hi' ? industry.labelHi || industry.labelEn : industry.labelEn;
  }
  return t(`onboarding.industries.${industry.key}`);
}

/**
 * What to call an industry key a theme is filed under - `general` included -
 * from `list`, else its translated name, else the key made readable.
 */
export function industryKeyLabel(
  key: string,
  list: readonly Industry[],
  t: (key: string) => string,
  language: string,
): string {
  const industry = findIndustry(key, list);
  if (industry) return industryLabel(industry, t, language);
  const translated = t(`onboarding.industries.${key}`);
  if (translated !== key) return translated;
  const words = key.replace(/_/g, ' ');
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/**
 * The industries on their sector shelves, labelled through `t` - the shape
 * `SelectField`'s `groups` takes.
 */
export function industryGroups(
  t: (key: string) => string,
  list: readonly Industry[] = INDUSTRIES,
  language = 'en',
) {
  return SECTORS.map((sector) => ({
    label: t(`onboarding.sectors.${sector}`),
    options: list
      .filter((industry) => industry.sector === sector)
      .map((industry) => ({ value: industry.key, label: industryLabel(industry, t, language) })),
  }));
}

const industrySchema = z.object({
  key: z.string(),
  sector: z.enum(SECTORS),
  business_type: z.string(),
  has_own_starters: z.boolean(),
  label_en: z.string().nullable(),
  label_hi: z.string().nullable(),
  active: z.boolean(),
  position: z.number(),
});

/**
 * The industries a merchant can pick. `include` adds the store's own one when
 * it has been retired or hidden, so the store's picker can still show it.
 */
export async function fetchIndustries(include?: string | null): Promise<Industry[]> {
  const query = include ? `?include=${encodeURIComponent(include)}` : '';
  const rows = await apiRequest(`/industries${query}`, z.array(industrySchema));
  return rows.map((row) => ({
    key: row.key,
    sector: row.sector,
    labelEn: row.label_en,
    labelHi: row.label_hi,
    active: row.active,
  }));
}

/**
 * The current list, with the built-ins in its place until it loads (or if it
 * fails): a picker is never empty, and never waits on the network to open.
 */
export function useIndustries(include?: string | null): readonly Industry[] {
  const query = useQuery({
    queryKey: ['industries', include ?? null],
    queryFn: () => fetchIndustries(include),
    staleTime: 5 * 60_000,
    retry: false,
  });
  return query.data ?? INDUSTRIES;
}

/** {@link industryKeyLabel} on the current list and language: names theme industry chips. */
export function useIndustryKeyLabel(): (key: string) => string {
  const list = useIndustries();
  const t = useT();
  const { language } = useI18n();
  return useCallback((key: string) => industryKeyLabel(key, list, t, language), [list, t, language]);
}
